import React, { useState } from 'react';
import { Send, Phone, MapPin, Mail, MessageSquare, CheckCircle2, ArrowRight, Clock, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import SEOHead, { ADVISOR_NAME, ADVISOR_PHONE, ADVISOR_PHONE_DISPLAY, ADVISOR_EMAIL } from '../components/SEOHead';
import { trackWhatsAppClick, trackPhoneClick, trackConsultationSubmit } from '../services/analytics';

const Contact = () => {
  const { t, i18n } = useTranslation();
  const isTe = i18n.language === 'te';
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', policyType: 'general', message: '' });
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    trackConsultationSubmit('contact_page_form', formData.policyType);

    setTimeout(() => {
      try {
        const inquiries = JSON.parse(localStorage.getItem('vsr_inquiries') || '[]');
        inquiries.push({ ...formData, date: new Date().toISOString() });
        localStorage.setItem('vsr_inquiries', JSON.stringify(inquiries));
      } catch (err) {
        console.error('Inquiry storage error:', err);
      }
      setIsSubmitting(false);
      setSubmitted(true);
      setFormData({ name: '', email: '', phone: '', policyType: 'general', message: '' });
    }, 800);
  };

  const contactSchema = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    'name': isTe ? 'వి. ఎస్. రెడ్డిని సంప్రదించండి | VSR Insurance Plus' : 'Contact VSR Insurance Plus | V. S. Reddy',
    'description': isTe
      ? 'హైదరాబాద్ మరియు ఎన్నారైల కోసం ఎల్ఐసి లైఫ్ ఇన్సూరెన్స్ మరియు హెచ్‌డిఎఫ్‌సి ఎర్గో హెల్త్ ఇన్సూరెన్స్ సలహా కోసం వి. ఎస్. రెడ్డిని సంప్రదించండి.'
      : 'Schedule an insurance consultation with V. S. Reddy. In-person meetings in Hyderabad and remote online guidance for NRIs worldwide.',
    'mainEntity': {
      '@type': 'InsuranceAgency',
      'name': 'VSR Insurance Plus',
      'telephone': ADVISOR_PHONE,
      'email': ADVISOR_EMAIL,
      'address': {
        '@type': 'PostalAddress',
        'streetAddress': 'SVR LIC Services, Dilsukhnagar / Hyderabad Central',
        'addressLocality': 'Hyderabad',
        'addressRegion': 'Telangana',
        'postalCode': '500060',
        'addressCountry': 'IN'
      },
      'openingHours': 'Mo-Sa 09:00-20:00'
    }
  };

  const nriWaMsg = isTe
    ? 'నమస్కారం వి. ఎస్. రెడ్డి గారు, నేను ఎన్నారైని. ఎల్ఐసి మరియు హెచ్‌డిఎఫ్‌సి ఎర్గో హెల్త్ ఇన్సూరెన్స్ వివరాలు తెలుసుకోవాలనుకుంటున్నాను.'
    : 'Hi V. S. Reddy, I am an NRI interested in LIC and health insurance planning. Please share the details.';

  return (
    <div className="min-h-screen pt-28 pb-20 bg-slate-50 dark:bg-slate-950">
      <SEOHead
        title={isTe ? 'సంప్రదించండి | వి. ఎస్. రెడ్డి - ఎల్ఐసి & హెల్త్ ఇన్సూరెన్స్ సలహాదారు' : 'Contact V. S. Reddy | Book LIC & Health Insurance Consultation'}
        description={isTe ? '25+ సంవత్సరాల అనుభవం గల సీనియర్ బీమా సలహాదారు వి. ఎస్. రెడ్డితో అపాయింట్‌మెంట్ బుక్ చేసుకోండి. కాల్ లేదా వాట్సాప్: +91-9849664800.' : 'Book a personalized insurance consultation with V. S. Reddy (25+ years experience). Call or WhatsApp +91-9849664800. Serving Hyderabad, Telangana, Pan-India & Global NRIs.'}
        canonicalPath="/contact"
        schemaJson={contactSchema}
        breadcrumbs={[{ name: isTe ? 'సంప్రదించండి' : 'Contact Us', path: '/contact' }]}
      />

      <div className="container mx-auto px-6 max-w-6xl">
        <div className="text-center max-w-2xl mx-auto mb-16">
          <div className="inline-flex items-center justify-center p-3 bg-blue-100 dark:bg-blue-900/30 rounded-full mb-6">
            <MessageSquare className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-slate-900 dark:text-white mb-6">
            {t('lets_start_planning')}
          </h1>
          <p className="text-lg text-slate-600 dark:text-slate-400">
            {t('contact_desc')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Info */}
          <div className="space-y-8">
            <div className="bg-white dark:bg-slate-900 p-8 rounded-3xl shadow-sm border border-slate-200 dark:border-slate-800">
              <h2 className="text-xl font-bold text-slate-900 dark:text-white mb-6">{t('direct_contact')}</h2>

              <div className="space-y-6">
                <a
                  href={`tel:${ADVISOR_PHONE}`}
                  onClick={() => trackPhoneClick('contact_page_phone')}
                  className="flex items-start gap-4 group cursor-pointer"
                >
                  <div className="w-12 h-12 rounded-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center flex-shrink-0 group-hover:bg-primary transition-colors">
                    <Phone className="w-5 h-5 text-slate-600 dark:text-slate-400 group-hover:text-white" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white mb-1">{t('call_whatsapp')}</h3>
                    <p className="text-slate-500 font-medium group-hover:text-primary transition-colors">{ADVISOR_PHONE_DISPLAY}</p>
                    <span className="text-xs text-emerald-600 dark:text-emerald-400 font-medium">
                      {isTe ? 'వి. ఎస్. రెడ్డి గారి ప్రత్యక్ష ఫోన్ నంబర్' : `Direct Line to ${ADVISOR_NAME}`}
                    </span>
                  </div>
                </a>

                <div className="flex items-start gap-4 group">
                  <div className="w-12 h-12 rounded-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                    <Mail className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white mb-1">{t('email_inquiries')}</h3>
                    <a href={`mailto:${ADVISOR_EMAIL}`} className="text-slate-500 hover:text-primary transition-colors">{ADVISOR_EMAIL}</a>
                  </div>
                </div>

                <div className="flex items-start gap-4 group">
                  <div className="w-12 h-12 rounded-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white mb-1">{t('office')}</h3>
                    <p className="text-slate-500 leading-relaxed">
                      {isTe ? (
                        <>
                          ఎస్వీఆర్ ఎల్ఐసి సర్వీసెస్<br />
                          హైదరాబాద్, తెలంగాణ, భారతదేశం<br />
                          <span className="text-xs text-slate-400">హైదరాబాద్, సికింద్రాబాద్, తెలంగాణ & ప్రపంచవ్యాప్త ఎన్నారైలకు సేవలు</span>
                        </>
                      ) : (
                        <>
                          SVR LIC Services<br />
                          Hyderabad, Telangana, India<br />
                          <span className="text-xs text-slate-400">Serving Hyderabad, Secunderabad, Telangana & Global NRIs</span>
                        </>
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4 group border-t border-slate-100 dark:border-slate-800 pt-4">
                  <div className="w-12 h-12 rounded-full bg-slate-50 dark:bg-slate-800 flex items-center justify-center flex-shrink-0">
                    <Clock className="w-5 h-5 text-slate-600 dark:text-slate-400" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900 dark:text-white mb-1">
                      {isTe ? 'కన్సల్టేషన్ సమయాలు' : 'Consultation Hours'}
                    </h3>
                    <p className="text-slate-500 text-sm">
                      {isTe ? (
                        <>
                          సోమవారం నుండి శనివారం: ఉదయం 9:00 - రాత్రి 8:00 IST<br />
                          ఆదివారం: ముందస్తు అపాయింట్‌మెంట్ ద్వారా (అత్యవసర క్లెయిమ్ సపోర్ట్ 24/7)
                        </>
                      ) : (
                        <>
                          Monday to Saturday: 9:00 AM - 8:00 PM IST<br />
                          Sunday: By appointment (Urgent claim support 24/7)
                        </>
                      )}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-primary to-blue-600 p-8 rounded-3xl text-white shadow-lg">
              <h3 className="text-xl font-bold mb-4">{t('are_you_nri')}</h3>
              <p className="text-blue-100 mb-6 leading-relaxed">
                {t('nri_desc')} {isTe ? 'ఎంబసీ అటెస్టేషన్, డీటీఏఏ నిబంధనలు మరియు విదేశీ కరెన్సీ చెల్లింపులలో పూర్తి సాయం అందిస్తాము.' : 'We handle foreign currency remittances, Embassy attestation guidance, and DTAA documentation remotely.'}
              </p>
              <a
                href={`https://wa.me/919849664800?text=${encodeURIComponent(nriWaMsg)}`}
                target="_blank"
                rel="noreferrer"
                onClick={() => trackWhatsAppClick('contact_nri_banner')}
                className="inline-flex items-center gap-2 px-6 py-3 bg-white text-primary rounded-xl font-bold hover:bg-slate-50 transition-colors shadow-md"
              >
                {t('ping_whatsapp')} <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Form */}
          <div className="bg-white dark:bg-slate-900 p-8 md:p-10 rounded-3xl shadow-xl shadow-slate-200/50 dark:shadow-none border border-slate-200 dark:border-slate-800">
            {submitted ? (
              <div className="h-full flex flex-col items-center justify-center text-center py-12">
                <div className="w-16 h-16 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-500 rounded-full flex items-center justify-center mb-6">
                  <CheckCircle2 className="w-8 h-8" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">{t('message_sent')}</h3>
                <p className="text-slate-500 max-w-sm mb-4">{t('will_reach_out')}</p>
                <div className="flex items-center gap-2 text-xs text-slate-400 bg-slate-50 dark:bg-slate-800 px-4 py-2 rounded-lg mb-6">
                  <ShieldCheck className="w-4 h-4 text-emerald-500" />
                  <span>{isTe ? 'మీ వివరాలు 100% సురక్షితం. ఎలాంటి స్పామ్ కాల్స్ ఉండవు.' : 'Your details are 100% confidential. No spam calls.'}</span>
                </div>
                <button
                  onClick={() => setSubmitted(false)}
                  className="text-primary font-semibold hover:underline"
                >
                  {t('send_another')}
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="flex justify-between items-center mb-2">
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-white">{t('send_message')}</h3>
                  <span className="text-xs text-slate-400 font-medium">{isTe ? 'ఉచితం & గోప్యమైనది' : 'Free & Confidential'}</span>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label htmlFor="contact-name" className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                      {t('your_name')} *
                    </label>
                    <input
                      id="contact-name"
                      required
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary text-slate-900 dark:text-white"
                      placeholder={isTe ? 'ఉదా. రమేష్ రెడ్డి' : 'e.g. Ramesh Reddy'}
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="contact-phone" className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                      {t('phone_number')} *
                    </label>
                    <input
                      id="contact-phone"
                      required
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary text-slate-900 dark:text-white"
                      placeholder="+91 98496 64800"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label htmlFor="contact-email" className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                      {t('email_address')} *
                    </label>
                    <input
                      id="contact-email"
                      required
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary text-slate-900 dark:text-white"
                      placeholder="ramesh@example.com"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="contact-policy" className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                      {isTe ? 'ఆసక్తి గల విభాగం' : 'Area of Interest'}
                    </label>
                    <select
                      id="contact-policy"
                      value={formData.policyType}
                      onChange={(e) => setFormData({ ...formData, policyType: e.target.value })}
                      className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary text-slate-900 dark:text-white"
                    >
                      {isTe ? (
                        <>
                          <option value="lic_life">ఎల్ఐసి జీవిత బీమా (జీవన్ ఉత్సవ్ / ఆనంద్)</option>
                          <option value="child_education">పిల్లల ఉన్నత విద్య & భవిష్యత్తు ప్రణాళిక</option>
                          <option value="pension">పెన్షన్ & రిటైర్‌మెంట్ ప్లానింగ్</option>
                          <option value="health_insurance">హెచ్‌డిఎఫ్‌సి ఎర్గో హెల్త్ ఇన్సూరెన్స్</option>
                          <option value="parents_health">వయోవృద్ధ తల్లిదండ్రుల హెల్త్ ఇన్సూరెన్స్</option>
                          <option value="nri_insurance">ఎన్నారై లైఫ్ & హెల్త్ ఇన్సూరెన్స్</option>
                          <option value="claim_support">ప్రస్తుత పాలసీ సర్వీసింగ్ / క్లెయిమ్ సాయం</option>
                        </>
                      ) : (
                        <>
                          <option value="lic_life">LIC Life Insurance (Jeevan Utsav / Anand)</option>
                          <option value="child_education">Child Education & Marriage Planning</option>
                          <option value="pension">Pension & Retirement Planning</option>
                          <option value="health_insurance">HDFC ERGO Health Insurance</option>
                          <option value="parents_health">Health Insurance for Senior Parents</option>
                          <option value="nri_insurance">NRI Life & Health Insurance</option>
                          <option value="claim_support">Existing Policy Servicing / Claim</option>
                        </>
                      )}
                    </select>
                  </div>
                </div>

                <div className="space-y-2">
                  <label htmlFor="contact-message" className="text-sm font-semibold text-slate-700 dark:text-slate-300">
                    {t('how_can_we_help')} *
                  </label>
                  <textarea
                    id="contact-message"
                    required
                    rows="4"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    className="w-full px-4 py-3 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl focus:ring-2 focus:ring-primary text-slate-900 dark:text-white resize-none"
                    placeholder={isTe ? 'మీ కుటుంబ బీమా అవసరాలు లేదా ప్రశ్నలను ఇక్కడ తెలియజేయండి...' : 'Tell us about your coverage goals or family requirements...'}
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full py-4 bg-slate-900 hover:bg-slate-800 dark:bg-white dark:hover:bg-slate-200 text-white dark:text-slate-900 rounded-xl font-bold flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                >
                  {isSubmitting ? (isTe ? 'పంపుతోంది...' : 'Sending Request...') : t('send_message_btn')} <Send className="w-4 h-4" />
                </button>

                <p className="text-xs text-slate-400 text-center">
                  {isTe ? (
                    <>సమర్పించడం ద్వారా, మీరు మా <a href="/privacy" className="underline hover:text-primary">గోప్యతా విధానం</a>కు అంగీకరిస్తున్నారు. మీ సమాచారం ఎవరితోనూ పంచుకోబడదు.</>
                  ) : (
                    <>By submitting, you agree to our <a href="/privacy" className="underline hover:text-primary">Privacy Policy</a>. VSR Insurance Plus never shares your personal information.</>
                  )}
                </p>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Contact;
